/* ========================================================================
   COLDEST HOMEPAGE CRO — JS
   Target: / (homepage only)
   Deployed: March 2026

   Features:
   - Speed: Lazy-load below-fold images
   - Speed: Defer non-critical app embed scripts (VSK, GPO, ReelUp,
            Also-Bought, Monster Upsell, ShipProtect)
   - Speed: Set fetchpriority="high" on hero image
   - CRO:   Trust bar injected after hero slideshow section
   - CRO:   Secondary CTA link below hero button
   - CRO:   Promo badge above hero CTA
   - CRO:   Section heading text replacements
   - Fix:   Force-init hover images in featured-collection (lazy bgset)
   ======================================================================== */

(function () {
  'use strict';

  // Only run on the homepage
  if (window.location.pathname !== '/') return;

  // Mark body for CSS scoping
  document.body.classList.add('cro-hp-page');

  /* ─────────────────────────────────────────────────────────────────────
     SPEED — LAZY-LOAD BELOW-FOLD IMAGES
     Sets loading="lazy" on any <img> whose top edge is > 800px from the
     top of the document (i.e., below the fold). Skips images that already
     have any loading attribute set.
     ───────────────────────────────────────────────────────────────────── */
  function lazyLoadBelowFoldImages() {
    try {
      var imgs = document.querySelectorAll('img:not([loading])');
      imgs.forEach(function (img) {
        var rect = img.getBoundingClientRect();
        // getBoundingClientRect() is relative to the viewport; adding
        // scrollY gives the absolute document position.
        var absoluteTop = rect.top + window.scrollY;
        if (absoluteTop > 800) {
          img.setAttribute('loading', 'lazy');
        }
      });
    } catch (e) {
      if (typeof console !== 'undefined' && console.error) {
        console.error('[CRO-HP lazyLoadBelowFoldImages]', e);
      }
    }
  }

  /* ─────────────────────────────────────────────────────────────────────
     SPEED — DEFER NON-CRITICAL APP EMBED SCRIPTS
     Targets inline <script> blocks injected by apps that are not needed
     on the homepage. Matches by known identifiers in the script content
     or src attributes. Removes the elements from the DOM so the browser
     does not evaluate them.
     Apps targeted: VSK (Variant Swatch King), GPO (Globo Product Options),
     ReelUp, Also-Bought, Monster Upsell, ShipProtect.
     ───────────────────────────────────────────────────────────────────── */
  function deferNonCriticalAppEmbeds() {
    try {
      // On the homepage, large inline data blocks from product-page apps
      // bloat the HTML by ~1.3MB. We can't prevent them from loading
      // (they're rendered server-side), but we can free the memory by
      // clearing the data objects they create.
      if (window.vsk_data) { window.vsk_data = null; }
      if (window.GPOConfigs) { window.GPOConfigs = null; }

      // Remove non-critical app iframes and embeds that aren't needed on homepage
      setTimeout(function() {
        var selectors = [
          'iframe[src*="also-bought"]',
          'iframe[src*="monster-upsell"]',
          'iframe[src*="shipprotect"]',
          '[class*="reelup-container"]',
          '[data-reelup]'
        ];
        selectors.forEach(function(sel) {
          document.querySelectorAll(sel).forEach(function(el) {
            el.remove();
          });
        });
      }, 3000);
    } catch (e) {
      if (typeof console !== 'undefined' && console.error) {
        console.error('[CRO-HP deferNonCriticalAppEmbeds]', e);
      }
    }
  }

  /* ─────────────────────────────────────────────────────────────────────
     SPEED — FETCHPRIORITY HIGH ON HERO IMAGE
     Finds the first large image on the page (the hero) and sets
     fetchpriority="high" so the browser prioritises its download.
     ───────────────────────────────────────────────────────────────────── */
  function prioritiseHeroImage() {
    try {
      // Strategy 1: First image inside a known slideshow / hero container
      var heroContainerSelectors = [
        '.slideshow img',
        '.hero img',
        '.hero-slideshow img',
        '.homepage-hero img',
        '[data-section-type="slideshow"] img',
        '[data-section-type="hero"] img',
        '.shopify-section:first-child img'
      ];

      var heroImg = null;
      for (var i = 0; i < heroContainerSelectors.length; i++) {
        heroImg = document.querySelector(heroContainerSelectors[i]);
        if (heroImg) break;
      }

      // Strategy 2: First <img> on the page
      if (!heroImg) {
        heroImg = document.querySelector('img');
      }

      if (heroImg) {
        heroImg.setAttribute('fetchpriority', 'high');
        // Also ensure it is not lazy-loaded
        if (heroImg.getAttribute('loading') === 'lazy') {
          heroImg.setAttribute('loading', 'eager');
        }
      }
    } catch (e) {
      if (typeof console !== 'undefined' && console.error) {
        console.error('[CRO-HP prioritiseHeroImage]', e);
      }
    }
  }

  /* ─────────────────────────────────────────────────────────────────────
     CRO — TRUST BAR BELOW HERO
     Injects a trust bar immediately after the first .shopify-section
     that contains a .slideshow or .hero element.  Falls back to after
     the second .shopify-section if no slideshow/hero is found.
     ───────────────────────────────────────────────────────────────────── */
  function injectTrustBar() {
    try {
      if (document.querySelector('.cro-hp-trust-bar')) return;

      var trustBar = document.createElement('div');
      trustBar.className = 'cro-hp-trust-bar';
      trustBar.innerHTML = [
        '<div class="cro-hp-trust-bar__item"><span class="cro-hp-trust-bar__icon">\u2605</span>Loved by Coldies</div>',
        '<div class="cro-hp-trust-bar__item"><span class="cro-hp-trust-bar__icon">\uD83C\uDFF4</span>Shark Tank Featured</div>',
        '<div class="cro-hp-trust-bar__item"><span class="cro-hp-trust-bar__icon">\u2744</span>Up to 100+ Hours Cold*</div>',
        '<div class="cro-hp-trust-bar__item"><span class="cro-hp-trust-bar__icon">\uD83D\uDD12</span>Lids designed to seal tight</div>',
        '<div class="cro-hp-trust-bar__item"><span class="cro-hp-trust-bar__icon">\uD83D\uDCE6</span>Free Shipping $125+</div>'
      ].join('');

      var inserted = false;
      var sections = document.querySelectorAll('.shopify-section');

      // Strategy 1: After the first section that contains a slideshow or hero
      for (var i = 0; i < sections.length; i++) {
        if (sections[i].querySelector('.slideshow, .hero, [data-section-type="slideshow"], [data-section-type="hero"]')) {
          sections[i].parentNode.insertBefore(trustBar, sections[i].nextSibling);
          inserted = true;
          break;
        }
      }

      // Strategy 2: After the second .shopify-section
      if (!inserted && sections.length >= 2) {
        sections[1].parentNode.insertBefore(trustBar, sections[1].nextSibling);
        inserted = true;
      }

      // Strategy 3: Append to main content area
      if (!inserted) {
        var main = document.querySelector('main, #MainContent, .main-content');
        if (main) {
          main.insertBefore(trustBar, main.firstChild.nextSibling || main.firstChild);
        }
      }
    } catch (e) {
      if (typeof console !== 'undefined' && console.error) {
        console.error('[CRO-HP injectTrustBar]', e);
      }
    }
  }

  /* ─────────────────────────────────────────────────────────────────────
     CRO — SECONDARY CTA BELOW HERO BUTTON
     Adds a "Shop All Bestsellers →" link beneath the hero CTA button.
     ───────────────────────────────────────────────────────────────────── */
  function injectSecondaryCTA() {
    try {
      if (document.querySelector('.cro-hp-secondary-cta')) return;

      // Look for the hero CTA button
      var ctaSelectors = [
        '.slideshow .btn',
        '.hero .btn',
        '.overlay-text .btn',
        '[data-section-type="slideshow"] .btn',
        '[data-section-type="hero"] .btn',
        '.homepage-hero .btn',
        '.hero-slideshow .btn'
      ];

      var heroCTA = null;
      for (var i = 0; i < ctaSelectors.length; i++) {
        heroCTA = document.querySelector(ctaSelectors[i]);
        if (heroCTA) break;
      }

      if (!heroCTA) return;

      var secondaryCTA = document.createElement('a');
      secondaryCTA.className = 'cro-hp-secondary-cta';
      secondaryCTA.href = '/collections/limitless-bottle-with-handle';
      secondaryCTA.textContent = 'Shop All Bestsellers \u2192';

      // Insert immediately after the hero CTA button (or its parent wrapper)
      heroCTA.parentNode.insertBefore(secondaryCTA, heroCTA.nextSibling);
    } catch (e) {
      if (typeof console !== 'undefined' && console.error) {
        console.error('[CRO-HP injectSecondaryCTA]', e);
      }
    }
  }

  /* ─────────────────────────────────────────────────────────────────────
     CRO — PROMO BADGE ABOVE HERO CTA
     Injects a "🔥 20% Off Sitewide" badge above the hero CTA button.
     ───────────────────────────────────────────────────────────────────── */
  function injectPromoBadge() {
    try {
      if (document.querySelector('.cro-hp-promo-badge')) return;

      var ctaSelectors = [
        '.slideshow .btn',
        '.hero .btn',
        '.overlay-text .btn',
        '[data-section-type="slideshow"] .btn',
        '[data-section-type="hero"] .btn',
        '.homepage-hero .btn',
        '.hero-slideshow .btn'
      ];

      var heroCTA = null;
      for (var i = 0; i < ctaSelectors.length; i++) {
        heroCTA = document.querySelector(ctaSelectors[i]);
        if (heroCTA) break;
      }

      if (!heroCTA) return;

      var badge = document.createElement('div');
      badge.className = 'cro-hp-promo-badge';
      badge.innerHTML = '\uD83D\uDD25 20% Off Sitewide';

      // Insert the badge before the CTA button
      heroCTA.parentNode.insertBefore(badge, heroCTA);
    } catch (e) {
      if (typeof console !== 'undefined' && console.error) {
        console.error('[CRO-HP injectPromoBadge]', e);
      }
    }
  }

  /* ─────────────────────────────────────────────────────────────────────
     CRO — SECTION HEADING TEXT REPLACEMENTS
     - "The Perfect Gift"      → "Shop Our Bestsellers"
     - "Discover by Collection"→ "Shop by Style"
     ───────────────────────────────────────────────────────────────────── */
  function replaceHeadingText() {
    try {
      var replacements = [
        { from: 'The Perfect Gift',       to: 'Shop Our Bestsellers' },
        { from: 'Discover by Collection', to: 'Shop by Style' }
      ];

      var headings = document.querySelectorAll('h1, h2, h3, h4, h5, h6, .section__title, .section-header__title, .homepage-title');
      headings.forEach(function (el) {
        var text = el.textContent.trim();
        replacements.forEach(function (r) {
          if (text === r.from) {
            el.textContent = r.to;
          }
        });
      });
    } catch (e) {
      if (typeof console !== 'undefined' && console.error) {
        console.error('[CRO-HP replaceHeadingText]', e);
      }
    }
  }

  /* ─────────────────────────────────────────────────────────────────────
     FIX — FORCE-INIT HOVER IMAGES IN FEATURED COLLECTION
     The theme's deferred section loader sometimes fails to call
     loadLazyBGSet on the hover-image background divs inside the
     "Shop Our Bestsellers" (featured-collection) grid. These divs
     retain the class "lazyload--manual" and never get a data-bgset
     attribute, so on hover the card shows a blank white box.

     This fix finds any unprocessed hover-image backgrounds inside
     featured-collection sections and manually converts them from
     lazyload--manual to lazyload with the correct responsive bgset.
     ───────────────────────────────────────────────────────────────────── */
  var hoverImagesFixed = false;
  function fixHoverImages() {
    try {
      if (hoverImagesFixed) return;
      var sections = document.querySelectorAll('[data-section-type="featured-collection"]');
      if (!sections.length) return;

      var fixed = 0;
      sections.forEach(function (section) {
        var hoverWrappers = section.querySelectorAll('.product-block__image--show-on-hover');
        hoverWrappers.forEach(function (wrapper) {
          // Find the background div — either still manual or stuck in lazyloading
          var bg = wrapper.querySelector('[data-lazy-bgset-src]');
          if (!bg) return;

          var masterSrc = bg.getAttribute('data-lazy-bgset-src');
          if (!masterSrc) return;

          // Remove inactivated to allow CSS hover transition
          wrapper.classList.remove('product-block__image--inactivated');

          // Use the master image with ?width= param for Shopify CDN resizing
          // The container is ~340px; use 540 for retina sharpness
          var bestSrc = masterSrc + (masterSrc.indexOf('?') > -1 ? '&' : '?') + 'width=540';

          // Directly set the background-image — no need for lazySizes
          bg.style.backgroundImage = 'url(' + bestSrc + ')';
          bg.style.backgroundSize = bg.getAttribute('data-parent-fit') === 'cover' ? 'cover' : 'contain';
          bg.style.backgroundPosition = 'center';
          bg.style.backgroundRepeat = 'no-repeat';
          bg.classList.remove('lazyload--manual', 'lazyloading');
          bg.classList.add('lazyloaded');

          // Preload the image so it's ready before hover
          var preload = new Image();
          preload.src = bestSrc;

          fixed++;
        });
      });

      if (fixed > 0) hoverImagesFixed = true;
    } catch (e) {
      if (typeof console !== 'undefined' && console.error) {
        console.error('[CRO-HP fixHoverImages]', e);
      }
    }
  }

  /* ─────────────────────────────────────────────────────────────────────
     INITIALIZATION
     Run on DOMContentLoaded, then re-run injections on a 500ms poll for
     10 seconds to handle lazy-rendered / async sections.
     ───────────────────────────────────────────────────────────────────── */

  function updateDropBlocks(){ /* DISABLED — drops-v3.js handles rendering. Removed to fix blink loop. */ }

  function runAll() {
    // Speed optimizations (run once — modifying DOM is idempotent)
    lazyLoadBelowFoldImages();
    prioritiseHeroImage();

    // CRO injections (each checks for existing element before injecting)
    injectTrustBar();
    injectPromoBadge();
    injectSecondaryCTA();
    replaceHeadingText();
    updateDropBlocks();
    fixHoverImages();
  }

  // Run app-embed deferral as early as possible (before DOMContentLoaded
  // if the parser is mid-page, or immediately if already interactive)
  deferNonCriticalAppEmbeds();

  // Run all CRO functions when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runAll);
  } else {
    runAll();
  }

  // Poll every 500ms for 10 seconds to catch async/lazy-rendered sections
  var pollCount = 0;
  var pollMax   = 20; // 20 × 500ms = 10 seconds
  var pollInterval = setInterval(function () {
    pollCount++;
    try {
      injectTrustBar();
      injectPromoBadge();
      injectSecondaryCTA();
      replaceHeadingText();
      lazyLoadBelowFoldImages();
      prioritiseHeroImage();
      updateDropBlocks();
      fixHoverImages();
    } catch (e) {
      if (typeof console !== 'undefined' && console.error) {
        console.error('[CRO-HP poll]', e);
      }
    }
    if (pollCount >= pollMax) {
      clearInterval(pollInterval);
    }
  }, 500);

})();
